import { useParticipation } from "../../../../shared/useParticipation";
import { isFinanceDirectory, useFinanceDirectory } from "../../../../shared/useFinanceDirectory";
import { AcademicCohortFields } from "../../../../shared/AcademicCohortFields";
import { TenancyActionFields } from "../../../../shared/TenancyActionFields";
import { AcademicCourseFields } from "../../../../shared/AcademicCourseFields";
import { AcademicProgramFields } from "../../../../shared/AcademicProgramFields";
import {
  AcademicCurriculumFields,
  academicCurriculumActionLabel,
  type AcademicCurriculumAction,
} from "../../../../shared/AcademicCurriculumFields";
import { CommercialActionFields } from "../../../../shared/CommercialActionFields";
import {
  isAcademicCurriculumDirectory,
  isAcademicCohortDirectory,
  isAcademicDefinitionDirectory,
  isAcademicCourseDirectory,
  useAcademicLifecycle,
  isAcademicDirectory,
  useAcademicDirectory,
  useAcademicHistory,
  type AcademicDirectory,
} from "../../../../shared/useAcademicCatalogue";
import {
  useTenancyDirectory,
  useTenancyHistory,
  isTenancyDirectory,
  tenancyFormValues,
  type TenancyDirectory,
} from "../../../../shared/useTenancyDirectory";
import { IdentityAssignmentFields } from "../../../../shared/IdentityAssignmentFields";
import { selectIdentityWorkspace } from "../../../../shared/identityClient";
import { workspaceClients } from "../../../../shared/workspaceAccess";
import { isIdentityDirectory, useIdentityDirectory } from "../../../../shared/useIdentityDirectory";
import { useIdentity, useSignedInIdentity } from "../../../../shared/useIdentity";
import { useCoreNotifications } from "../../../../shared/useCoreNotifications";
import { useInstitutionLabAnalytics } from "../../../../shared/useInstitutionLabAnalytics";
import { ProgramAnnouncementPreference } from "../../../../shared/ProgramAnnouncementPreference";
import {
  isCommercialDirectory,
  useCommercialDirectory,
  useCommercialHistory,
  type CommercialDirectory,
} from "../../../../shared/useCommercialCatalogue";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import academyLogo from "../../../../shared/brand/chatoner-ai-academy-logo-landscape.png";
import academyMark from "../../../../shared/brand/chatoner-ai-academy-mark.png";
import { platformEnvironment } from "../../config/environment";
import { AREAS, INSTITUTION_ROLES, LANGUAGES, PAGE_SPECS } from "../../data/mockData";
import { signOut } from "../../services/authSession";
import { dropdownThemeStyles, STYLES, V2_STYLES } from "../../styles/theme";
import type {
  DetailState,
  ModalState,
  RoleProfile,
  RouteDef,
  Toast,
  WorkspaceRow,
  WorkspaceSpec,
} from "../../types/domain";
import { csvEscape, currentTime, downloadFile, safeRead, safeWrite } from "../../utils/browser";
import { resolveInstitutionLocation, writeInstitutionLocation } from "../../utils/navigation";

const API_BASE = (
  (globalThis as any).__CHATONER_ACADEMY_API_BASE__ || platformEnvironment.apiBaseUrl
).replace(/\/$/, "");
const BUILD_VERSION = "2.0.0";

function WorkspaceTabPanel({
  title,
  activeTab,
  recordCount,
}: {
  title: string;
  activeTab: string;
  recordCount: number;
}) {
  const normalized = activeTab.toLowerCase();
  const focus = normalized.includes("context")
    ? "Scope, purpose and the current workspace context are in focus."
    : normalized.includes("relationship")
      ? "Linked people, permissions and responsibility boundaries are in focus."
      : normalized.includes("sponsor")
        ? "Funding, sponsor visibility and support relationships are in focus."
        : normalized.includes("transition") || normalized.includes("handoff")
          ? "Ownership changes, handoffs and the next safe action are in focus."
          : normalized.includes("timeline")
            ? "Recent activity and upcoming actions are in focus."
            : `The ${activeTab.toLowerCase()} view is in focus for this workspace.`;
  return (
    <section className="workspace-tab-panel" role="tabpanel" aria-label={`${activeTab} view`}>
      <div className="workspace-tab-panel-icon">
        <Icon name="grid" size={18} />
      </div>
      <div className="workspace-tab-panel-copy">
        <span>Current workspace view</span>
        <strong>{activeTab}</strong>
        <p>{focus}</p>
      </div>
      <div className="workspace-tab-panel-meta">
        <b>{recordCount}</b>
        <span>records in {title.toLowerCase()}</span>
      </div>
    </section>
  );
}

import {
  AccessBoundary,
  Button,
  Card,
  FilterToolbar,
  Icon,
  LayoutRenderer,
  Metrics,
  Notes,
  Status,
  WorkspaceHero,
} from "../../components/institution/WorkspaceLayouts";

const safeGet = safeRead;
const safeSet = safeWrite;
function roleHas(role: RoleProfile, key: "areas" | "manage" | "approve", area: string) {
  return role[key].includes("*") || role[key].includes(area);
}

function WorkspacePage({
  spec,
  tenancy,
  academic,
  commercial,
  role,
  records,
  setRecords,
  onToast,
  onModal,
  onNavigate,
  onSwitchRole,
  onDetail,
}: {
  spec: WorkspaceSpec;
  tenancy: TenancyDirectory;
  academic: AcademicDirectory;
  commercial: CommercialDirectory;
  role: RoleProfile;
  records: WorkspaceRow[];
  setRecords: (rows: WorkspaceRow[]) => void;
  onToast: (title: string, message: string, tone?: Toast["tone"]) => void;
  onModal: (modal: ModalState) => void;
  onNavigate: (area: string, page: string) => void;
  onSwitchRole: () => void;
  onDetail: (detail: DetailState) => void;
}) {
  const identityAccount = useIdentity();
  const identityPage = isIdentityDirectory(spec.id);
  const identityPermission = ["people-roles", "people-access", "roles-permissions"].includes(spec.id)
    ? "identity.role.grant"
    : "identity.invitation.create";
  const curriculumDirectory = isAcademicCurriculumDirectory(spec.id);
  const curriculumCanManage = !!identityAccount.session?.permissions.includes(
    "institution.curriculum.manage",
  );
  const curriculumCanApprove = !!identityAccount.session?.permissions.includes(
    "institution.curriculum.approve",
  );
  const curriculumCanPublish = !!identityAccount.session?.permissions.includes(
    "institution.curriculum.publish",
  );
  const legacyCanView = roleHas(role, "areas", spec.area),
    legacyCanManage = roleHas(role, "manage", spec.area),
    canApprove = isFinanceDirectory(spec.id)
      ? false
      : academic.enabled
        ? curriculumDirectory
          ? curriculumCanApprove || curriculumCanPublish
          : curriculumCanApprove
        : roleHas(role, "approve", spec.area);
  const canView =
    isFinanceDirectory(spec.id) ||
    academic.enabled ||
    commercial.enabled ||
    tenancy.enabled ||
    identityPage ||
    spec.id === "settings-security"
      ? true
      : legacyCanView;
  // D03 controls use current backend permissions and the approved existing dialog fields.
  const commercialPermission =
    commercial.kind === "licenses" ? "commercial.license.manage" : "commercial.funding.manage";
  const canManage = isFinanceDirectory(spec.id)
    ? false
    : commercial.enabled
      ? !!identityAccount.session?.permissions.some(
          permission => permission === "*" || permission === commercialPermission,
        )
      : academic.enabled
        ? curriculumDirectory
          ? spec.id === "curriculum-maps"
            ? curriculumCanManage
            : curriculumCanManage || curriculumCanApprove || curriculumCanPublish
          : !!identityAccount.session?.permissions.includes(
              isAcademicCohortDirectory(spec.id)
                ? "catalogue.cohort.manage"
                : "institution.curriculum.manage",
            )
        : tenancy.enabled
          ? tenancy.canManage
          : identityPage
            ? !!identityAccount.session?.permissions.some(p => p === "*" || p === identityPermission)
            : legacyCanManage;
  const institutionLabAnalytics = useInstitutionLabAnalytics(spec.id === "labs-analytics" && canView);
  const [query, setQuery] = useState(""),
    [status, setStatus] = useState("All"),
    [tab, setTab] = useState(spec.tabs[0]);
  const [formValues, setFormValues] = useState<Record<string, string>>(() =>
    isFinanceDirectory(spec.id) || tenancy.enabled || commercial.enabled
      ? {}
      : safeGet(`institution-form-${spec.id}`, {}),
  );
  const [policyToggles, setPolicyToggles] = useState<Record<string, boolean>>(() =>
    safeGet(`institution-policy-${spec.id}`, {}),
  );
  useEffect(() => {
    setQuery("");
    setStatus("All");
    setTab(spec.tabs[0]);
    if (!isFinanceDirectory(spec.id) && !tenancy.enabled && !commercial.enabled)
      setFormValues(safeGet(`institution-form-${spec.id}`, {}));
    setPolicyToggles(safeGet(`institution-policy-${spec.id}`, {}));
  }, [commercial.enabled, spec.id, spec.tabs, tenancy.enabled]);
  useEffect(() => {
    if (tenancy.enabled)
      setFormValues(tenancy.profile ? tenancyFormValues("institution", tenancy.profile) : {});
  }, [tenancy.context, tenancy.enabled, tenancy.profile]);
  const filtered = useMemo(
    () =>
      records.filter(
        row =>
          (status === "All" || String(row.status) === status) &&
          Object.values(row).some(v => String(v).toLowerCase().includes(query.toLowerCase())),
      ),
    [records, status, query],
  );
  const exportRows = () => {
    const cols = spec.columns.map(c => c.key);
    const csv = [
      cols.map(csvEscape).join(","),
      ...records.map(r => cols.map(c => csvEscape(r[c])).join(",")),
    ].join("\n");
    downloadFile(`${spec.id}.csv`, csv, "text/csv");
    onToast("Export downloaded", `${spec.title} was exported with ${records.length} records.`);
  };
  const primary = () => {
    if (!canManage) {
      onToast("Read-only role", `${role.name} cannot create or change ${spec.title}.`, "warning");
      return;
    }
    if (tenancy.profilePage) {
      void saveForm();
      return;
    }
    onModal({ kind: "create", payload: { spec, ...(curriculumDirectory ? { records } : {}) } });
  };
  const move = (id: string, next: string) => {
    if (isFinanceDirectory(spec.id)) {
      onToast("Action unavailable", "This finance action requires an approved server command.", "warning");
      return;
    }
    if (commercial.enabled) {
      onToast(
        "Governed commercial action required",
        "Use an approved commercial command to change this record.",
        "warning",
      );
      return;
    }
    if (academic.enabled && curriculumDirectory) {
      const record = records.find(row => String(row.id) === id);
      if (!record) return;
      const action: AcademicCurriculumAction =
        next === "Active"
          ? "publish"
          : next === "Approved"
            ? "approve"
            : next === "Needs attention" || next === "Rejected"
              ? "request-changes"
              : String(record.status) === "draft"
                ? "submit"
                : String(record.status) === "in_review"
                  ? "approve"
                  : String(record.status) === "approved"
                    ? "publish"
                    : "clone";
      onModal({ kind: "create", payload: { spec, record, records, academicAction: action } });
      return;
    }
    if (academic.enabled && isAcademicDefinitionDirectory(spec.id)) {
      const record = records.find(row => String(row.id) === id);
      if (record)
        onModal({
          kind: "create",
          payload: {
            spec,
            record,
            academicAction:
              next === "Active"
                ? "publish"
                : next === "Approved"
                  ? "approve"
                  : next === "Needs attention" || next === "Rejected"
                    ? "request-changes"
                    : "submit",
          },
        });
      return;
    }
    if (academic.enabled) {
      const record = records.find(row => String(row.id) === id);
      if (record) onModal({ kind: "create", payload: { spec, record } });
      return;
    }
    if (tenancy.enabled) {
      onToast(
        "Governed tenancy action required",
        "Use the entity membership or structure controls for this record.",
        "warning",
      );
      return;
    }
    if (isIdentityDirectory(spec.id)) {
      onToast(
        "Identity state is managed by the Academy",
        "Use the invitation or role-assignment dialog to change access.",
        "warning",
      );
      return;
    }
    if (!canManage) {
      onToast("Read-only role", "This role cannot change workflow state.", "warning");
      return;
    }
    setRecords(records.map(r => (String(r.id) === id ? { ...r, status: next, updated: "Just now" } : r)));
    onToast("Workflow updated", `${spec.title} record moved to ${next}.`);
  };
  const advance = (id: string) => move(id, "Ready");
  const decision = (id: string, next: string) => {
    if (!canApprove) {
      onToast("Approval authority required", `${role.name} cannot make this decision.`, "warning");
      return;
    }
    move(id, next);
  };
  const saveForm = async () => {
    if (!canManage) return;
    if (tenancy.enabled) {
      try {
        await tenancy.saveProfile(formValues, formValues["Approved change reason"] || "");
        onToast("Profile saved", "The current entity profile was updated.");
      } catch (error) {
        onToast("Unable to save profile", error instanceof Error ? error.message : "Try again.", "warning");
      }
      return;
    }
    safeSet(`institution-form-${spec.id}`, formValues);
    safeSet(`institution-policy-${spec.id}`, policyToggles);
    onToast(
      "Governed changes saved",
      `${spec.title} settings were stored locally; production uses the NestJS API and audit log.`,
    );
  };
  const test = () =>
    onToast(
      "Connection diagnostics passed",
      `${spec.title} returned healthy authentication, authorization, latency and schema checks.`,
    );
  const handoff = () => {
    const domain = spec.external?.domain || "institutions.academy.chatoner.com";
    onToast(
      "Governed handoff unavailable",
      `${domain} has no server-issued Institution authority contract; no context or user data was sent.`,
      "warning",
    );
  };
  if (!canView)
    return (
      <AccessBoundary
        spec={spec}
        role={role}
        onSwitchRole={onSwitchRole}
        onRequestAccess={() => onModal({ kind: "help", payload: { spec } })}
      />
    );
  if (spec.id === "labs-analytics") {
    const analytics = institutionLabAnalytics.data;
    const completed = analytics?.values.completed;
    return (
      <div
        className="workspace-root"
        data-route-id={spec.id}
        data-layout={spec.layout}
        data-signature={spec.signature}
        data-d16-analytics-state="connected"
      >
        <span className="route-signature">{spec.signature}</span>
        <Card
          title={spec.title}
          subtitle="Privacy-thresholded verified Lab completions for the current Institution workspace only."
          actions={
            <Button
              icon="refresh"
              onClick={() => void institutionLabAnalytics.refresh()}
              disabled={institutionLabAnalytics.loading}
            >
              Refresh
            </Button>
          }
        >
          {institutionLabAnalytics.error ? (
            <div className="empty" role="alert">
              Governed analytics unavailable: {institutionLabAnalytics.error}
            </div>
          ) : completed !== undefined ? (
            <div className="metric-card">
              <div className="metric-value">{completed}</div>
              <div className="metric-label">Current unrevoked verified Lab completions</div>
              <p>D10 evidence through the verified D17 Labs callback</p>
            </div>
          ) : !institutionLabAnalytics.loading ? (
            <div className="empty">
              {analytics?.availability === "not_projected"
                ? "No Institution-scoped projection has been published yet."
                : "No publishable cell is available; a small count remains suppressed."}
            </div>
          ) : (
            <div className="empty">Loading governed projection…</div>
          )}
        </Card>
        <Card title="Metric governance">
          {analytics ? (
            <div>
              <b>
                {analytics.metricKey} v{analytics.definitionVersion}
              </b>
              <p>
                Source {analytics.sourceDomain} · minimum cell {analytics.minimumCellCount} · suppressed cells{" "}
                {analytics.suppressedCells}
              </p>
              <p>
                Adoption, utilization, cost, safety, capacity and outcome trends are unavailable. This
                projection grants no transaction or export authority.
              </p>
            </div>
          ) : (
            <div className="empty">Metric definition unavailable.</div>
          )}
        </Card>
      </div>
    );
  }
  if (spec.area === "analytics" || spec.id === "labs-analytics")
    return (
      <div
        className="workspace-root"
        data-route-id={spec.id}
        data-layout={spec.layout}
        data-signature={spec.signature}
        data-d16-analytics-state="unavailable"
      >
        <span className="route-signature">{spec.signature}</span>
        <Card
          title={spec.title}
          subtitle="This view is waiting for approved metric definitions, purpose-limited owner facts and freshness evidence."
        >
          <div className="empty">
            No sample metrics, reports or exports are shown. Academy Analytics will enable this route after
            its source, scope, suppression and freshness contract is certified.
          </div>
        </Card>
      </div>
    );
  return (
    <div
      className="workspace-root"
      data-route-id={spec.id}
      data-layout={spec.layout}
      data-signature={spec.signature}
    >
      <span className="route-signature">{spec.signature}</span>
      <WorkspaceHero spec={spec} canManage={canManage} onPrimary={primary} onExport={exportRows} />
      <div className={`permission-banner ${canManage ? "" : "readonly"}`}>
        <span className="icon-wrap">
          <Icon name={canManage ? "shield" : "eye"} size={16} />
        </span>
        <div>
          <b>
            {academic.enabled || commercial.enabled || tenancy.enabled || identityPage
              ? `${identityAccount.session?.roles.join(", ") || "Current account"} · ${canManage ? "Management available" : "Directory access"}`
              : canManage
                ? `${role.name} can manage this workspace`
                : `${role.name} is viewing this workspace read-only`}
          </b>
          <p>
            {academic.enabled || commercial.enabled || tenancy.enabled || identityPage ? (
              `Scope: ${tenancy.profile?.name || identityAccount.session?.workspace.name || "Current workspace"}. Each action is checked against your current backend permissions.`
            ) : (
              <>
                Scope: {role.scope}.{" "}
                {canApprove
                  ? "This role also has approval authority here."
                  : "Approval actions require a separately authorized role."}{" "}
                Backend enforcement remains authoritative.
              </>
            )}
          </p>
        </div>
        <span className="toolbar-spacer" />
        <span className="backend-chip">
          <i className="online-dot" />
          {commercial.enabled
            ? commercial.error
              ? "Unable to load commercial records"
              : commercial.loading
                ? "Loading commercial records"
                : "Current commercial records"
            : academic.enabled
              ? academic.error
                ? "Unable to load academic records"
                : academic.loading
                  ? "Loading academic records"
                  : "Current academic records"
              : tenancy.enabled
                ? tenancy.error
                  ? "Unable to load records"
                  : tenancy.loading
                    ? "Loading records"
                    : "Current entity records"
                : "NestJS · Kafka · Redis ready"}
        </span>
      </div>
      <div className="workspace-tabs">
        {spec.tabs.map(item => (
          <button
            type="button"
            className={`workspace-tab ${tab === item ? "active" : ""}`}
            key={item}
            onClick={() => setTab(item)}
          >
            {item}
          </button>
        ))}
      </div>
      <WorkspaceTabPanel title={spec.title} activeTab={tab} recordCount={filtered.length} />
      {spec.id === "settings-notifications" && (
        <div className="card">
          <ProgramAnnouncementPreference />
        </div>
      )}
      {spec.layout !== "form" && spec.layout !== "analytics" ? (
        <FilterToolbar
          query={query}
          setQuery={setQuery}
          status={status}
          setStatus={setStatus}
          spec={{ ...spec, rows: records }}
          onExport={exportRows}
          onSavedView={() =>
            onToast("Saved view created", `${query || "All"} / ${status} was saved for ${spec.title}.`)
          }
        />
      ) : null}
      <Metrics items={spec.metrics} />
      <LayoutRenderer
        spec={spec}
        rows={filtered}
        onOpen={row => onDetail({ routeId: spec.id, row })}
        onPrimary={primary}
        onExport={exportRows}
        onMove={move}
        onAdvance={advance}
        onDecision={decision}
        onSaveForm={saveForm}
        formValues={formValues}
        setFormValues={setFormValues}
        policyToggles={policyToggles}
        setPolicyToggles={setPolicyToggles}
        onTest={test}
        onHandoff={handoff}
        onNavigate={onNavigate}
        canManage={canManage}
      />
    </div>
  );
}

function DetailDrawer({
  detail,
  spec,
  role,
  onClose,
  onUpdate,
  onEdit,
  onToast,
}: {
  detail: DetailState;
  spec: WorkspaceSpec;
  role: RoleProfile;
  onClose: () => void;
  onUpdate: (row: WorkspaceRow) => void;
  onEdit: (spec: WorkspaceSpec, row: WorkspaceRow, academicAction?: string) => void;
  onToast: (title: string, message: string, tone?: Toast["tone"]) => void;
}) {
  const account = useIdentity();
  const tenancyHistory = useTenancyHistory(
    "institution",
    detail?.routeId || "",
    detail ? String(detail.row.id) : undefined,
  );
  const academicHistory = useAcademicHistory(
    detail?.routeId || "",
    detail ? String(detail.row.parentId || detail.row.id) : undefined,
    detail ? String(detail.row.parentKind || "") : undefined,
  );
  const commercialHistory = useCommercialHistory(
    "institution",
    detail?.routeId || "",
    detail ? String(detail.row.id) : undefined,
  );
  const history = academicHistory.enabled
    ? academicHistory
    : commercialHistory.enabled
      ? commercialHistory
      : tenancyHistory;
  const lifecycle = useAcademicLifecycle(detail?.routeId || "", detail ? String(detail.row.id) : undefined);
  if (!detail) return null;
  const row = detail.row;
  const curriculumDirectory = isAcademicCurriculumDirectory(spec.id);
  const curriculumState = String(row.status);
  const curriculumCanManage = !!account.session?.permissions.includes("institution.curriculum.manage");
  const curriculumCanApprove = !!account.session?.permissions.includes("institution.curriculum.approve");
  const curriculumCanPublish = !!account.session?.permissions.includes("institution.curriculum.publish");
  const previewCanManage = roleHas(role, "manage", spec.area),
    previewCanApprove = roleHas(role, "approve", spec.area);
  const commercialPermission =
    commercialHistory.enabled && spec.id === "lab-entitlements"
      ? "commercial.license.manage"
      : "commercial.funding.manage";
  const canManage = isFinanceDirectory(spec.id)
    ? false
    : commercialHistory.enabled
      ? !!account.session?.permissions.some(
          permission => permission === "*" || permission === commercialPermission,
        )
      : academicHistory.enabled
        ? curriculumDirectory
          ? curriculumCanManage && !["in_review", "approved"].includes(curriculumState)
          : !!account.session?.permissions.includes(
              isAcademicCohortDirectory(spec.id)
                ? "catalogue.cohort.manage"
                : "institution.curriculum.manage",
            ) && !["retired", "archived"].includes(curriculumState)
        : history.enabled
          ? !!account.session?.permissions.some(
              permission =>
                permission === `tenancy.institution.${row.personId ? "members" : "structure"}.manage` ||
                (!row.personId && permission === "tenancy.institution.members.manage"),
            )
          : !isIdentityDirectory(spec.id) && previewCanManage;
  const canPublish = curriculumDirectory
    ? curriculumState === "approved" && curriculumCanPublish
    : !!lifecycle.data?.canPublish && curriculumCanPublish;
  const canApprove = isFinanceDirectory(spec.id)
    ? false
    : commercialHistory.enabled
      ? false
      : academicHistory.enabled
        ? curriculumDirectory
          ? curriculumState === "in_review" && curriculumCanApprove
          : !!lifecycle.data?.canReview && curriculumCanApprove
        : !history.enabled && !isIdentityDirectory(spec.id) && previewCanApprove;
  const change = (status: string) => {
    onUpdate({ ...row, status, updated: "Just now" });
    onToast("Record updated", `${String(row.name)} is now ${status}.`);
    onClose();
  };
  return (
    <aside className="detail-drawer-v2" aria-label={`${String(row.name)} details`}>
      <div className="drawer-head-v2">
        <span className="list-icon">
          <Icon name={spec.icon} />
        </span>
        <span className="head-copy">
          <b>{String(row.name)}</b>
          <span>
            {spec.title} · {String(row.id)}
          </span>
        </span>
        <Status>{String(row.status)}</Status>
        <button type="button" className="icon-button" onClick={onClose} aria-label="Close details">
          <Icon name="close" />
        </button>
      </div>
      <div className="detail-body-v2">
        <div className="detail-fields">
          {Object.entries(row).map(([key, value]) => (
            <div className="detail-field" key={key}>
              <span>{key.replace(/-/g, " ")}</span>
              <b>{String(value)}</b>
            </div>
          ))}
        </div>
        <Card
          title="Authority and history"
          subtitle={
            academicHistory.enabled
              ? "Latest recorded academic changes"
              : history.enabled
                ? "Recorded changes in this entity"
                : isFinanceDirectory(spec.id)
                  ? "Finance audit evidence"
                  : "Attributable demonstration timeline"
          }
          className=""
        >
          {isFinanceDirectory(spec.id) ? (
            <p>Finance audit history is not included in this view.</p>
          ) : history.enabled ? (
            <div className="timeline" aria-live="polite">
              {history.loading && <p>Loading recorded history…</p>}
              {history.error && <p role="alert">{history.error}</p>}
              {!history.loading && !history.error && !history.items.length && (
                <p>No recorded changes are available.</p>
              )}
              {history.items.map(item => (
                <div className="timeline-item" key={item.id}>
                  <b>{item.action}</b>
                  <p>{item.reason}</p>
                  <p>
                    Actor: {item.actorId}
                    {item.effectiveActorId ? ` · Effective actor: ${item.effectiveActorId}` : ""}
                  </p>
                  <p>
                    {item.fromStatus ? `${item.fromStatus} → ` : ""}
                    {item.toStatus}
                    {item.version !== null ? ` · Version ${item.version}` : ""}
                  </p>
                  <time>{new Date(item.createdAt).toLocaleString()}</time>
                </div>
              ))}
            </div>
          ) : (
            <div className="timeline">
              <div className="timeline-item">
                <b>Record opened</b>
                <p>
                  {role.name} · {role.scope}
                </p>
                <time>{currentTime()}</time>
              </div>
              <div className="timeline-item">
                <b>Last status change</b>
                <p>
                  {String(row.owner)} · {String(row.updated)}
                </p>
                <time>{String(row.status)}</time>
              </div>
              <div className="timeline-item">
                <b>Policy evaluated</b>
                <p>Role, scope, purpose, segregation of duties and data sensitivity</p>
                <time>Passed</time>
              </div>
            </div>
          )}
        </Card>
        <Card title="Connected context">
          <Notes spec={spec} />
        </Card>
      </div>
      <div className="detail-foot-v2">
        <Button
          icon="edit"
          disabled={!canManage}
          onClick={() =>
            history.enabled
              ? onEdit(spec, row, curriculumDirectory && curriculumState === "draft" ? "submit" : undefined)
              : change("Active")
          }
        >
          Edit / activate
        </Button>
        <Button
          icon="check"
          variant="green"
          disabled={!canApprove && !canPublish}
          onClick={() =>
            academicHistory.enabled
              ? onEdit(spec, row, canPublish ? "publish" : "approve")
              : change("Approved")
          }
        >
          {canPublish ? "Publish" : "Approve"}
        </Button>
        <Button
          icon="alert"
          variant="orange"
          disabled={academicHistory.enabled ? !canApprove : history.enabled || !canManage}
          onClick={() =>
            academicHistory.enabled ? onEdit(spec, row, "request-changes") : change("Needs attention")
          }
        >
          Request changes
        </Button>
        <Button
          icon="history"
          onClick={() =>
            history.enabled
              ? history.refresh()
              : onToast(
                  "Audit history opened",
                  `${String(row.name)} audit history is available to authorized roles.`,
                )
          }
        >
          Audit
        </Button>
      </div>
    </aside>
  );
}

function ActionModal({
  modal,
  records,
  onClose,
  onCreate,
  onSaved,
  onToast,
  role: _role,
  onRoleChange: _onRoleChange,
}: {
  modal: ModalState;
  records: WorkspaceRow[];
  onClose: () => void;
  onCreate: (spec: WorkspaceSpec, row: WorkspaceRow) => void;
  onSaved: () => void;
  onToast: (title: string, message: string, tone?: Toast["tone"]) => void;
  role: RoleProfile;
  onRoleChange: (role: RoleProfile) => void;
}) {
  const account = useIdentity();
  const [tenancyBusy, setTenancyBusy] = useState(false);
  const close = () => {
    if (!tenancyBusy) onClose();
  };
  const assignedGrants =
    account.access?.grants.filter(grant => grant.workspaceId === account.session?.workspace.id) || [];
  const [name, setName] = useState(""),
    [owner, setOwner] = useState("Academic Registry"),
    [scope, setScope] = useState("Whole institution"),
    [status, setStatus] = useState("Draft");
  if (!modal) return null;
  const spec: WorkspaceSpec | undefined = modal.payload?.spec;
  let title: string, subtitle: string, body: React.ReactNode, foot: React.ReactNode;
  let icon = "plus";
  if (modal.kind === "create" && spec && isAcademicCurriculumDirectory(spec.id)) {
    const action = modal.payload?.academicAction as AcademicCurriculumAction | undefined;
    const effectiveAction = action || (spec.id === "curriculum-maps" ? "clone" : undefined);
    title = modal.payload?.record
      ? `Curriculum action · ${String(modal.payload.record.name)}`
      : spec.primaryAction;
    subtitle =
      "Select the exact academic version and record the attributable reason for this governed action.";
    body = (
      <AcademicCurriculumFields
        key={`${spec.id}:${String(modal.payload?.record?.id || "select")}:${effectiveAction || "infer"}`}
        route={spec.id}
        action={effectiveAction}
        record={modal.payload?.record}
        records={records}
        formId="academic-curriculum-form"
        onSaved={onSaved}
        onBusy={setTenancyBusy}
      />
    );
    foot = (
      <>
        <Button disabled={tenancyBusy} onClick={close}>
          Cancel
        </Button>
        <button
          className="btn btn-primary"
          type="submit"
          form="academic-curriculum-form"
          disabled={tenancyBusy}
        >
          {tenancyBusy
            ? "Recording…"
            : effectiveAction
              ? academicCurriculumActionLabel[effectiveAction]
              : "Record curriculum action"}
        </button>
      </>
    );
  } else if (modal.kind === "create" && spec && isAcademicCohortDirectory(spec.id)) {
    title = modal.payload?.record ? `Manage ${String(modal.payload.record.name)}` : spec.primaryAction;
    subtitle = "Use the exact published academic versions, schedule and a recorded change reason.";
    body = (
      <AcademicCohortFields
        kind="institution"
        record={modal.payload?.record}
        action={modal.payload?.academicAction}
        formId="academic-cohort-form"
        onSaved={onSaved}
        onBusy={setTenancyBusy}
      />
    );
    foot = (
      <>
        <Button disabled={tenancyBusy} onClick={close}>
          Cancel
        </Button>
        <button type="submit" form="academic-cohort-form" className="btn btn-primary" disabled={tenancyBusy}>
          {tenancyBusy ? "Recording…" : modal.payload?.record ? "Save changes" : "Create cohort"}
        </button>
      </>
    );
  } else if (modal.kind === "create" && spec && isAcademicCourseDirectory(spec.id)) {
    title = modal.payload?.record ? `Edit ${String(modal.payload.record.name)}` : spec.primaryAction;
    subtitle = "Save the academic draft in your current Institution. Published content uses a new version.";
    body = (
      <AcademicCourseFields
        action={modal.payload?.academicAction}
        record={modal.payload?.record}
        formId="academic-course-form"
        onSaved={onSaved}
        onBusy={setTenancyBusy}
      />
    );
    foot = (
      <>
        <Button disabled={tenancyBusy} onClick={close}>
          Cancel
        </Button>
        <button className="btn btn-primary" type="submit" form="academic-course-form" disabled={tenancyBusy}>
          {tenancyBusy
            ? "Recording…"
            : modal.payload?.academicAction
              ? modal.payload.academicAction === "publish"
                ? "Publish"
                : modal.payload.academicAction === "submit"
                  ? "Submit for review"
                  : "Record decision"
              : modal.payload?.record
                ? "Save changes"
                : "Create record"}
        </button>
      </>
    );
  } else if (modal.kind === "create" && spec?.id === "program-portfolio") {
    title = modal.payload?.record ? `Edit ${String(modal.payload.record.name)}` : spec.primaryAction;
    subtitle = "Save a Program draft in your current Institution. Published versions retain their history.";
    body = (
      <AcademicProgramFields
        action={modal.payload?.academicAction}
        record={modal.payload?.record}
        formId="academic-program-form"
        onSaved={onSaved}
        onBusy={setTenancyBusy}
      />
    );
    foot = (
      <>
        <Button disabled={tenancyBusy} onClick={close}>
          Cancel
        </Button>
        <button className="btn btn-primary" type="submit" form="academic-program-form" disabled={tenancyBusy}>
          {tenancyBusy
            ? "Recording…"
            : modal.payload?.academicAction
              ? "Record decision"
              : modal.payload?.record
                ? "Save changes"
                : "Create record"}
        </button>
      </>
    );
  } else if (modal.kind === "create" && spec && isCommercialDirectory("institution", spec.id)) {
    title = modal.payload?.record
      ? `Commercial action · ${String(modal.payload.record.name)}`
      : spec.primaryAction;
    subtitle = "Choose the exact governed commercial record, validity and attributable reason.";
    body = (
      <CommercialActionFields
        entityKind="institution"
        route={spec.id}
        record={modal.payload?.record}
        formId="commercial-portal-form"
        onSaved={onSaved}
        onBusy={setTenancyBusy}
      />
    );
    foot = (
      <>
        <Button disabled={tenancyBusy} onClick={close}>
          Cancel
        </Button>
        <button
          className="btn btn-primary"
          type="submit"
          form="commercial-portal-form"
          disabled={tenancyBusy}
        >
          {tenancyBusy ? "Recording…" : "Record commercial action"}
        </button>
      </>
    );
  } else if (
    modal.kind === "create" &&
    spec &&
    (isTenancyDirectory("institution", spec.id) || modal.payload?.record?.personId)
  ) {
    title = modal.payload?.record ? `Edit ${String(modal.payload.record.name)}` : spec.primaryAction;
    subtitle = "Choose the current scope and record a reason for this change.";
    body = (
      <TenancyActionFields
        kind="institution"
        route={spec.id}
        record={modal.payload?.record}
        formId="tenancy-portal-form"
        onSaved={onSaved}
        onBusy={setTenancyBusy}
      />
    );
    foot = (
      <>
        <Button disabled={tenancyBusy} onClick={close}>
          Cancel
        </Button>
        <button className="btn btn-primary" type="submit" form="tenancy-portal-form" disabled={tenancyBusy}>
          {tenancyBusy ? "Recording…" : modal.payload?.record ? "Save changes" : "Create record"}
        </button>
      </>
    );
  } else if (modal.kind === "create" && spec && isIdentityDirectory(spec.id)) {
    const operation = ["people-roles", "people-access", "roles-permissions"].includes(spec.id)
      ? "role-grant"
      : "invitation";
    title = operation === "invitation" ? "Invite workspace member" : "Assign workspace role";
    subtitle = "Choose a recipient, authorized role and scope, reason and expiry.";
    body = <IdentityAssignmentFields operation={operation} onClose={onClose} />;
    foot = null;
  } else if (modal.kind === "create" && spec) {
    title = spec.primaryAction;
    subtitle = `Create a distinct ${spec.title.toLowerCase()} record. Production validation and authorization remain server-enforced.`;
    body = (
      <div className="form-fields-v2">
        <div className="form-field-v2 full">
          <label>Name / title</label>
          <input
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={`New ${spec.title.toLowerCase()} item`}
          />
        </div>
        <div className="form-field-v2">
          <label>Owner</label>
          <select value={owner} onChange={e => setOwner(e.target.value)}>
            <option>Academic Registry</option>
            <option>Quality Directorate</option>
            <option>Digital Learning</option>
            <option>Student Success</option>
            <option>Research Office</option>
            <option>Finance Office</option>
          </select>
        </div>
        <div className="form-field-v2">
          <label>Scope</label>
          <select value={scope} onChange={e => setScope(e.target.value)}>
            <option>Whole institution</option>
            <option>School of Computing</option>
            <option>BAAI Year 3</option>
            <option>October 2026 cohort</option>
          </select>
        </div>
        <div className="form-field-v2">
          <label>Initial status</label>
          <select value={status} onChange={e => setStatus(e.target.value)}>
            <option>Draft</option>
            <option>Active</option>
            <option>Review due</option>
          </select>
        </div>
        <div className="form-field-v2">
          <label>Backend domain</label>
          <input readOnly value={`${API_BASE}/institution/${spec.area}/${spec.id}`} />
        </div>
      </div>
    );
    foot = (
      <>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          icon="plus"
          variant="primary"
          disabled={!name.trim() || isTenancyDirectory("institution", spec.id)}
          onClick={() => {
            onCreate(spec, {
              id: `${spec.id.toUpperCase().slice(0, 4)}-${Date.now().toString().slice(-5)}`,
              name: name.trim(),
              owner,
              scope,
              status,
              progress: 0,
              updated: "Just now",
              date: "Not scheduled",
              time: "—",
              people: "0",
              evidence: "0%",
              risk: "Low",
              category: spec.group,
              decision: "Pending",
              code: `NEW-${Date.now().toString().slice(-4)}`,
            });
            onClose();
          }}
        >
          Create record
        </Button>
      </>
    );
  } else if (modal.kind === "role") {
    title = "Assigned institution roles";
    subtitle = "Your active role grants and their assigned scopes.";
    icon = "users";
    body = (
      <div className="v2-stack">
        {assignedGrants.map(r => (
          <button
            type="button"
            className="role-option active"
            key={r.id}
            onClick={() => onToast(r.name, r.permissions.join(", ") || "No permission keys assigned", "info")}
          >
            <span className="role-option-icon">
              <Icon name="shield" />
            </span>
            <span>
              <b>{r.name}</b>
              <span>{r.scopeType ? `${r.scopeType} · ${r.scopeId}` : account.session?.workspace.name}</span>
            </span>
            <Icon name="check" />
          </button>
        ))}
      </div>
    );
    foot = <Button onClick={onClose}>Close</Button>;
  } else if (modal.kind === "institution") {
    title = "Switch education institution";
    subtitle = "Membership and role are independently verified for every institution.";
    icon = "building";
    body = (
      <div className="v2-stack">
        {(
          account.access?.memberships.filter(m =>
            workspaceClients(
              (account.access?.grants || []).filter(g => g.workspaceId === m.workspaceId).map(g => g.roleKey),
            ).includes("institutions"),
          ) || []
        )
          .map(m => [m.workspaceId, m.name, m.type])
          .map(item => (
            <button
              type="button"
              className="action-card"
              key={item[0]}
              onClick={async () => {
                try {
                  await selectIdentityWorkspace(item[0]);
                  window.location.reload();
                } catch (error) {
                  onToast(
                    "Workspace not changed",
                    error instanceof Error ? error.message : "Unable to switch workspace",
                    "warning",
                  );
                }
              }}
            >
              <span className="thread-avatar">{item[1].slice(0, 2).toUpperCase()}</span>
              <span>
                <strong>{item[1]}</strong>
                <span>{item[2]}</span>
              </span>
              <Icon name="arrow" />
            </button>
          ))}
      </div>
    );
    foot = <Button onClick={onClose}>Close</Button>;
  } else if (modal.kind === "help") {
    title = "Education Institution Hub help";
    subtitle = "Route-specific guidance and backend integration references.";
    icon = "help";
    body = (
      <div className="policy-list">
        <div className="policy-item">
          <Icon name="grid" />
          <span>
            <strong>126 distinct operational destinations</strong>
            <span>Every route has a registered layout, data model, actions and backend domain.</span>
          </span>
        </div>
        <div className="policy-item">
          <Icon name="users" />
          <span>
            <strong>Eleven institution roles</strong>
            <span>Role, delegated scope and approval authority remain separate.</span>
          </span>
        </div>
        <div className="policy-item">
          <Icon name="link" />
          <span>
            <strong>Connected Academy services</strong>
            <span>Class, Labs and Build Rooms retain independent authorization.</span>
          </span>
        </div>
        <div className="policy-item">
          <Icon name="database" />
          <span>
            <strong>NestJS backend ready</strong>
            <span>Configure __CHATONER_ACADEMY_API_BASE__ before production deployment.</span>
          </span>
        </div>
      </div>
    );
    foot = <Button onClick={onClose}>Close</Button>;
  } else {
    title = "Confirm governed action";
    subtitle = "Review the action, authority and audit reason before continuing.";
    icon = "shield";
    body = <Notes spec={spec || PAGE_SPECS["command-overview"]} />;
    foot = (
      <>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="primary"
          onClick={() => {
            onToast("Action confirmed", "The governed action was recorded.");
            onClose();
          }}
        >
          Confirm
        </Button>
      </>
    );
  }
  return (
    <div
      className="modal-v2"
      role="dialog"
      aria-modal="true"
      onMouseDown={e => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="modal-card-v2">
        <div className="modal-head-v2">
          <span className="modal-icon">
            <Icon name={icon} />
          </span>
          <span className="copy">
            <b>{title}</b>
            <span>{subtitle}</span>
          </span>
          <button
            type="button"
            className="icon-button"
            onClick={close}
            disabled={tenancyBusy}
            aria-label="Close dialog"
          >
            <Icon name="close" />
          </button>
        </div>
        <div className="modal-body-v2">{body}</div>
        <div className="modal-foot-v2">{foot}</div>
      </div>
    </div>
  );
}

function App() {
  const identity = useSignedInIdentity();
  const initialArea = safeGet("institution-v2-area", "command"),
    initialPage = safeGet("institution-v2-page", "command-overview"),
    initialRoleId = safeGet("institution-v2-role", "principal");
  const initialLocation = resolveInstitutionLocation(AREAS, PAGE_SPECS, {
    areaId: AREAS.some(a => a.id === initialArea) ? initialArea : "command",
    pageId: PAGE_SPECS[initialPage] ? initialPage : "command-overview",
  });
  const [activeArea, setActiveArea] = useState<string>(initialLocation.areaId);
  const [activePage, setActivePage] = useState<string>(initialLocation.pageId);
  const [activeRole, setActiveRole] = useState<RoleProfile>(
    INSTITUTION_ROLES.find(r => r.id === initialRoleId) || INSTITUTION_ROLES[0],
  );
  const [recordsByRoute, setRecordsByRoute] = useState<Record<string, WorkspaceRow[]>>(() =>
    Object.fromEntries(
      Object.entries(safeGet<Record<string, WorkspaceRow[]>>("institution-v2-records", {})).filter(
        ([id]) =>
          !isFinanceDirectory(id) &&
          !isTenancyDirectory("institution", id) &&
          !isAcademicDirectory(id) &&
          !isCommercialDirectory("institution", id),
      ),
    ),
  );
  const [mobileNav, setMobileNav] = useState(false),
    [searchOpen, setSearchOpen] = useState(false),
    [search, setSearch] = useState(""),
    [notificationsOpen, setNotificationsOpen] = useState(false),
    [languageOpen, setLanguageOpen] = useState(false),
    [profileOpen, setProfileOpen] = useState(false),
    [roleOpen, setRoleOpen] = useState(false);
  const [modal, setModal] = useState<ModalState>(null),
    [detail, setDetail] = useState<DetailState>(null),
    [toasts, setToasts] = useState<Toast[]>([]),
    [selectedNotification, setSelectedNotification] = useState<any | null>(null),
    [primaryLanguage, setPrimaryLanguage] = useState("English"),
    [secondaryLanguage, setSecondaryLanguage] = useState("Luganda");
  const toast = (title: string, message: string, tone: Toast["tone"] = "success") => {
    const id = Date.now() + Math.random();
    setToasts(v => [...v, { id, title, message, tone }].slice(-4));
    setTimeout(() => setToasts(v => v.filter(t => t.id !== id)), 3600);
  };
  const area = AREAS.find(a => a.id === activeArea) || AREAS[0];
  const route = area.routes.find(r => r.id === activePage) || area.routes[0];
  const directory = useIdentityDirectory(route.id);
  const tenancy = useTenancyDirectory("institution", route.id);
  const academic = useAcademicDirectory(route.id);
  const commercial = useCommercialDirectory("institution", route.id);
  const finance = useFinanceDirectory(route.id);
  const participation = useParticipation(
    ["students", "lab-entitlements", "cohorts-intakes"].includes(route.id),
    true,
  );
  const sourceSpec = PAGE_SPECS[route.id];
  const originalSpec =
    sourceSpec && participation.data
      ? {
          ...sourceSpec,
          notes: sourceSpec.notes.map((note, index) =>
            index === 0 ? { ...note, body: participation.data!.notice } : note,
          ),
        }
      : sourceSpec;
  if (!originalSpec) throw new Error(`Missing PAGE_SPECS entry for ${route.id}`);
  const spec = finance.enabled
    ? {
        ...originalSpec,
        rows: finance.rows,
        chart: [],
        metrics: originalSpec.metrics.map(metric => ({
          ...metric,
          value: "—",
          trend: finance.error || (finance.loading ? "Loading" : "Current scoped finance records"),
        })),
        notes: originalSpec.notes.map(note => ({ ...note, body: finance.error || finance.notice })),
      }
    : commercial.enabled
      ? {
          ...originalSpec,
          rows: commercial.rows,
          chart: [],
          metrics: originalSpec.metrics.map((metric, index) => ({
            ...metric,
            value:
              index === 0 && !commercial.loading && !commercial.error && !commercial.hasMore
                ? String(commercial.rows.length)
                : "—",
            trend:
              commercial.error ||
              (commercial.loading
                ? "Loading"
                : commercial.hasMore
                  ? "First 500 commercial records loaded"
                  : "Current commercial records"),
          })),
          notes: originalSpec.notes.map((note, index) =>
            index === 0
              ? {
                  ...note,
                  body:
                    commercial.error ||
                    (commercial.loading
                      ? "Loading current commercial records."
                      : commercial.kind === "licenses"
                        ? "License capacity and validity come from the Academy commercial catalogue. Allocation and usage remain owned by enrollment and learning."
                        : "Funding arrangements and their validity come from the Academy commercial catalogue. Awards, payments and balances remain owned by their downstream domains."),
                }
              : note,
          ),
        }
      : academic.enabled
        ? {
            ...originalSpec,
            rows: academic.rows,
            chart: [],
            metrics: originalSpec.metrics.map(metric => ({
              ...metric,
              value:
                academic.data &&
                !academic.data.hasMore &&
                ["Active records", "Active items"].includes(metric.label)
                  ? String(academic.data.items.filter(course => course.status === "published").length)
                  : "—",
              trend:
                academic.error ||
                (academic.loading
                  ? "Loading"
                  : academic.data?.hasMore
                    ? "First 500 academic records loaded"
                    : "Current academic records"),
            })),
            notes: originalSpec.notes.map((note, index) =>
              index === 0
                ? {
                    ...note,
                    body:
                      academic.error ||
                      (academic.loading
                        ? "Loading the academic catalogue."
                        : "Academic records come from the current Institution catalogue."),
                  }
                : isAcademicCurriculumDirectory(route.id)
                  ? {
                      ...note,
                      body: "Exact academic versions retain their recorded outcomes and structure. Attainment, assessment, Labs and credential evidence are managed separately.",
                    }
                  : note,
            ),
          }
        : tenancy.enabled
          ? {
              ...originalSpec,
              rows: tenancy.rows,
              chart: [],
              metrics: originalSpec.metrics.map(metric => ({
                ...metric,
                value:
                  tenancy.profile && ["Teams", "Active entities", "Legal entities"].includes(metric.label)
                    ? String(
                        metric.label === "Legal entities"
                          ? 1
                          : tenancy.rows.filter(row =>
                              metric.label === "Teams" ? row.unitType === "team" : row.status === "active",
                            ).length,
                      )
                    : "—",
                trend: tenancy.error || (tenancy.loading ? "Loading" : "Current entity records"),
              })),
              notes: originalSpec.notes.map((note, index) =>
                index === 0
                  ? {
                      ...note,
                      body:
                        tenancy.error ||
                        (tenancy.loading
                          ? "Loading current entity records."
                          : tenancy.profile
                            ? `${tenancy.profile.name} · ${tenancy.profile.status}`
                            : "No entity context is available."),
                    }
                  : note,
              ),
            }
          : directory.enabled
            ? {
                ...originalSpec,
                rows: directory.rows,
                metrics: originalSpec.metrics.map((metric, index) => ({
                  ...metric,
                  value: index === 0 && directory.data ? String(directory.rows.length) : "—",
                  trend:
                    directory.error ||
                    (directory.loading
                      ? "Loading"
                      : index === 0
                        ? directory.data?.hasMore
                          ? "First 500 records; refine before exporting"
                          : "Current workspace records"
                        : "Not reported"),
                })),
              }
            : originalSpec;
  const records = finance.enabled
    ? finance.rows
    : commercial.enabled
      ? commercial.rows
      : academic.enabled
        ? academic.rows
        : tenancy.enabled
          ? tenancy.rows
          : directory.enabled
            ? directory.rows
            : recordsByRoute[spec.id] || spec.rows;
  const setRecords = (rows: WorkspaceRow[]) =>
    !isFinanceDirectory(spec.id) &&
    !commercial.enabled &&
    !isAcademicDirectory(spec.id) &&
    setRecordsByRoute(current => ({ ...current, [spec.id]: rows }));
  const navigate = useCallback((areaId: string, pageId?: string) => {
    const next = AREAS.find(a => a.id === areaId) || AREAS[0];
    const pid = pageId && next.routes.some(r => r.id === pageId) ? pageId : next.routes[0].id;
    setActiveArea(next.id);
    setActivePage(pid);
    setMobileNav(false);
    setSearchOpen(false);
    setNotificationsOpen(false);
    setSelectedNotification(null);
    setLanguageOpen(false);
    setProfileOpen(false);
    setRoleOpen(false);
    setDetail(null);
    writeInstitutionLocation({ areaId: next.id, pageId: pid });
    window.scrollTo(0, 0);
  }, []);
  const coreInbox = useCoreNotifications();
  const notificationRecords = coreInbox.notifications.map(item => ({
    ...item,
    time: new Date(item.createdAt).toLocaleString(),
    unread: !item.readAt,
  }));
  const unread = coreInbox.unreadCount;
  useEffect(() => {
    safeSet("institution-v2-area", activeArea);
    safeSet("institution-v2-page", activePage);
    safeSet("institution-v2-role", activeRole.id);
  }, [activeArea, activePage, activeRole]);
  useEffect(() => {
    safeSet("institution-v2-records", recordsByRoute);
  }, [recordsByRoute]);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === "Escape") {
        setSearchOpen(false);
        setMobileNav(false);
        setNotificationsOpen(false);
        setLanguageOpen(false);
        setProfileOpen(false);
        setRoleOpen(false);
        setModal(null);
        setDetail(null);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);
  useEffect(() => {
    const sync = () => {
      const next = resolveInstitutionLocation(AREAS, PAGE_SPECS, {
        areaId: "command",
        pageId: "command-overview",
      });
      setActiveArea(next.areaId);
      setActivePage(next.pageId);
      setDetail(null);
    };
    window.addEventListener("hashchange", sync);
    window.addEventListener("popstate", sync);
    return () => {
      window.removeEventListener("hashchange", sync);
      window.removeEventListener("popstate", sync);
    };
  }, []);
  useEffect(() => {
    (window as any).__CHATONER_INSTITUTION_PLATFORM__ = {
      version: BUILD_VERSION,
      routeCount: Object.keys(PAGE_SPECS).length,
      layoutCount: new Set(Object.values(PAGE_SPECS).map(v => v.layout)).size,
      activeArea,
      activePage,
      activeRole: activeRole.id,
      apiBase: API_BASE,
    };
    (window as any).__CHATONER_INSTITUTION_NAVIGATE__ = (area: string, page: string) => navigate(area, page);
  }, [activeArea, activePage, activeRole, navigate]);
  const grouped = useMemo(
    () =>
      area.routes.reduce<Record<string, RouteDef[]>>((acc, r) => {
        (acc[r.group] ||= []).push(r);
        return acc;
      }, {}),
    [area],
  );
  const searchResults = useMemo(() => {
    const q = search.toLowerCase().trim();
    const routeResults = AREAS.flatMap(a =>
      a.routes.map(r => ({
        kind: "route",
        area: a,
        route: r,
        label: r.title,
        detail: `${a.label} · ${r.description}`,
      })),
    ).filter(x => !q || `${x.label} ${x.detail}`.toLowerCase().includes(q));
    const recordResults = Object.values(PAGE_SPECS)
      .flatMap(s =>
        (isFinanceDirectory(s.id)
          ? []
          : isCommercialDirectory("institution", s.id)
            ? s.id === spec.id
              ? commercial.rows
              : []
            : isAcademicDirectory(s.id)
              ? s.id === spec.id
                ? academic.rows
                : []
              : isIdentityDirectory(s.id)
                ? s.id === spec.id
                  ? directory.rows
                  : []
                : recordsByRoute[s.id] || s.rows
        )
          .slice(0, 3)
          .map(row => ({
            kind: "record",
            area: AREAS.find(a => a.id === s.area)!,
            route: AREAS.find(a => a.id === s.area)!.routes.find(r => r.id === s.id)!,
            label: String(row.name),
            detail: `${s.title} · ${String(row.owner)} · ${String(row.status)}`,
          })),
      )
      .filter(x => q && `${x.label} ${x.detail}`.toLowerCase().includes(q));
    return [...routeResults, ...recordResults].slice(0, 40);
  }, [search, recordsByRoute, spec.id, directory.rows, academic.rows, commercial.rows]);
  const createRecord = (target: WorkspaceSpec, row: WorkspaceRow) => {
    if (isFinanceDirectory(target.id) || isCommercialDirectory("institution", target.id)) {
      toast("Not saved", "Use an approved commercial command to create this record.", "warning");
      return;
    }
    if (isAcademicDirectory(target.id)) {
      toast("Not saved", "Use the current academic creation command for this record.", "warning");
      return;
    }
    if (isIdentityDirectory(target.id) || isTenancyDirectory("institution", target.id)) {
      toast(
        "Not saved",
        "This dialog needs the recipient, role and scope controls before it can submit an identity request.",
        "warning",
      );
      return;
    }
    const current = recordsByRoute[target.id] || target.rows;
    setRecordsByRoute(v => ({ ...v, [target.id]: [row, ...current] }));
    toast("Record created", `${String(row.name)} was added to ${target.title}.`);
  };
  const updateDetail = (row: WorkspaceRow) => {
    const target = PAGE_SPECS[detail?.routeId || spec.id];
    if (isFinanceDirectory(target.id) || isCommercialDirectory("institution", target.id)) {
      toast("Not saved", "Use an approved commercial command to change this record.", "warning");
      return;
    }
    if (isAcademicDirectory(target.id)) {
      toast("Not saved", "Use the current academic command to change this record.", "warning");
      return;
    }
    if (isIdentityDirectory(target.id) || isTenancyDirectory("institution", target.id)) {
      toast("Not saved", "Identity changes require the authorized identity action.", "warning");
      return;
    }
    const current = recordsByRoute[target.id] || target.rows;
    setRecordsByRoute(v => ({
      ...v,
      [target.id]: current.map(r => (String(r.id) === String(row.id) ? row : r)),
    }));
  };
  const switchRole = (role: RoleProfile) => {
    setActiveRole(role);
    safeSet("institution-v2-role", role.id);
  };
  const openDetail = (d: DetailState) => setDetail(d);
  const roleLabel = identity.role || "No assigned role";
  return (
    <div className="app-shell">
      <style>{STYLES + V2_STYLES + dropdownThemeStyles}</style>
      {mobileNav ? <div className="mobile-overlay" onClick={() => setMobileNav(false)} /> : null}
      <aside className={`rail ${mobileNav ? "mobile-open" : ""}`} aria-label="Education Institution areas">
        <button
          className="brand-mark"
          onClick={() => navigate("command", "command-overview")}
          aria-label="Institution Command"
          title="Institution Command"
        >
          <img
            className="academy-platform-mark"
            src={academyMark}
            alt=""
            width={837}
            height={765}
            decoding="async"
          />
          <span className="rail-tip">Institution Command</span>
        </button>
        <div className="rail-scroll">
          {AREAS.map(a => (
            <button
              key={a.id}
              className={`rail-button ${activeArea === a.id ? "active" : ""}`}
              onClick={() => navigate(a.id, a.routes[0].id)}
              aria-label={a.label}
              title={a.label}
            >
              <Icon name={a.icon} />
              {a.id === "command" ? <span className="rail-badge">12</span> : null}
              <span className="rail-tip">{a.label}</span>
            </button>
          ))}
        </div>
        <div className="rail-bottom">
          <button
            className="rail-button"
            onClick={() => setSearchOpen(true)}
            aria-label="Search"
            title="Search"
          >
            <Icon name="search" />
            <span className="rail-tip">Search</span>
          </button>
          <button
            className="rail-button"
            onClick={() => setModal({ kind: "help" })}
            aria-label="Help"
            title="Help"
          >
            <Icon name="help" />
            <span className="rail-tip">Help</span>
          </button>
        </div>
      </aside>
      <aside className={`sidebar ${mobileNav ? "mobile-open" : ""}`} aria-label={`${area.label} navigation`}>
        <div className="academy-platform-sidebar-brand">
          <img
            className="academy-platform-logo"
            src={academyLogo}
            alt="Chatoner AI Academy"
            width={4124}
            height={967}
            decoding="async"
          />
        </div>
        <div className="sidebar-head">
          <button className="institution-switch" onClick={() => setModal({ kind: "institution" })}>
            <span className="institution-avatar">{identity.initials}</span>
            <span className="institution-copy">
              <b>{directory.account.session?.workspace.name || "Academy workspace"}</b>
              <span>Education Institution Platform v{BUILD_VERSION}</span>
            </span>
            <Icon name="chevron" size={13} />
          </button>
        </div>
        <div className="sidebar-body">
          <div className="sidebar-title">
            <div>
              <h2>{area.label}</h2>
              <p>{area.routes.length} distinct operational destinations</p>
            </div>
            <button className="icon-button" onClick={() => setMobileNav(false)} aria-label="Close navigation">
              <Icon name="close" size={16} />
            </button>
          </div>
          {Object.entries(grouped).map(([group, routes]) => (
            <div className="nav-group" key={group}>
              <div className="nav-group-label">{group}</div>
              {routes.map(r => (
                <button
                  key={r.id}
                  className={`nav-link ${activePage === r.id ? "active" : ""}`}
                  onClick={() => navigate(area.id, r.id)}
                >
                  <Icon name={r.icon} size={15} />
                  <span className="nav-label">{r.title}</span>
                  {r.badge ? <span className="nav-badge">{r.badge}</span> : null}
                </button>
              ))}
            </div>
          ))}
          <div className="sidebar-card">
            <b>11 institution roles · 126 workspaces</b>
            <p>
              Every destination has its own operational model. Role, scope, purpose and approval are enforced
              by the backend.
            </p>
            <Button small icon="users" onClick={() => setModal({ kind: "role" })}>
              View assigned roles
            </Button>
          </div>
        </div>
      </aside>
      <main className="main-shell">
        <header className="topbar">
          <button
            className="icon-button mobile-menu"
            aria-label="Open navigation"
            onClick={() => setMobileNav(true)}
          >
            <Icon name="menu" />
          </button>
          <div className="breadcrumb">
            <div className="eyebrow">{area.label}</div>
            <h1>{route.title}</h1>
          </div>
          <div className="top-actions">
            <button className="search-trigger" onClick={() => setSearchOpen(true)}>
              <Icon name="search" size={15} />
              <span>Search all 126 workspaces…</span>
              <kbd>⌘K</kbd>
            </button>
            <div className="topbar-role">
              <button
                type="button"
                className="role-button"
                onClick={() => {
                  setRoleOpen(v => !v);
                  setLanguageOpen(false);
                  setNotificationsOpen(false);
                  setProfileOpen(false);
                }}
              >
                <span className="role-icon">
                  <Icon name="shield" size={14} />
                </span>
                <span className="role-button-copy">
                  <b>{roleLabel}</b>
                  <span>{activeRole.scope}</span>
                </span>
                <Icon name="chevron" size={12} />
              </button>
              {roleOpen ? (
                <div className="role-menu">
                  {INSTITUTION_ROLES.map(r => (
                    <button
                      type="button"
                      className={`role-option ${activeRole.id === r.id ? "active" : ""}`}
                      key={r.id}
                      onClick={() => {
                        switchRole(r);
                        setRoleOpen(false);
                        toast("Role preview changed", `${r.name} is now active.`);
                      }}
                    >
                      <span className="role-option-icon">
                        <Icon name="shield" />
                      </span>
                      <span>
                        <b>{r.name}</b>
                        <span>{r.scope}</span>
                      </span>
                      {activeRole.id === r.id ? <Icon name="check" /> : null}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <button
              className={`language-button ${languageOpen ? "active" : ""}`}
              onClick={() => {
                setLanguageOpen(v => !v);
                setRoleOpen(false);
                setProfileOpen(false);
                setNotificationsOpen(false);
              }}
            >
              <Icon name="globe" size={15} />
              <span>
                {LANGUAGES.find(l => l[0] === primaryLanguage)?.[1]} +{" "}
                {LANGUAGES.find(l => l[0] === secondaryLanguage)?.[1]}
              </span>
            </button>
            <button
              className={`icon-button ${notificationsOpen ? "active" : ""}`}
              aria-label="Institution notifications"
              onClick={() => {
                setNotificationsOpen(v => !v);
                setLanguageOpen(false);
                setRoleOpen(false);
                setProfileOpen(false);
              }}
            >
              <Icon name="bell" size={16} />
              {unread ? <span className="dot" /> : null}
            </button>
            <button
              className="profile-button"
              onClick={() => {
                setProfileOpen(v => !v);
                setLanguageOpen(false);
                setRoleOpen(false);
                setNotificationsOpen(false);
              }}
            >
              <span className="profile-avatar">{identity.initials}</span>
              <span className="profile-copy">
                <b>{identity.name}</b>
                <span>{identity.role || "No assigned role"}</span>
              </span>
              <Icon name="chevron" size={12} />
            </button>
          </div>
        </header>
        <div className="content">
          <WorkspacePage
            key={spec.id}
            spec={spec}
            tenancy={tenancy}
            academic={academic}
            commercial={commercial}
            role={activeRole}
            records={records}
            setRecords={setRecords}
            onToast={toast}
            onModal={setModal}
            onNavigate={navigate}
            onSwitchRole={() => setModal({ kind: "role" })}
            onDetail={openDetail}
          />
        </div>
      </main>
      <nav className="mobile-bottom" aria-label="Mobile navigation">
        {[
          ["command", "home", "Command"],
          ["people", "users", "People"],
          ["academics", "book", "Academics"],
          ["class", "video", "Class"],
          ["labs", "flask", "Labs"],
        ].map(([id, icon, label]) => (
          <button
            key={id}
            className={`mobile-nav-button ${activeArea === id ? "active" : ""}`}
            onClick={() => navigate(id, AREAS.find(a => a.id === id)!.routes[0].id)}
          >
            <Icon name={icon} size={18} />
            <span>{label}</span>
          </button>
        ))}
      </nav>
      {searchOpen ? (
        <div
          className="overlay"
          onMouseDown={e => {
            if (e.target === e.currentTarget) setSearchOpen(false);
          }}
        >
          <div className="search-modal">
            <div className="search-input-row">
              <Icon name="search" />
              <input
                autoFocus
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search routes, people, programs, records and decisions…"
              />
              <button className="icon-button" onClick={() => setSearchOpen(false)} aria-label="Close search">
                <Icon name="close" />
              </button>
            </div>
            <div className="search-results">
              {searchResults.length ? (
                searchResults.map((result: any, i) => (
                  <button
                    className="search-result"
                    key={`${result.route.id}-${result.label}-${i}`}
                    onClick={() => navigate(result.area.id, result.route.id)}
                  >
                    <span className="list-icon">
                      <Icon name={result.route.icon} />
                    </span>
                    <span className="search-result-copy">
                      <b>{result.label}</b>
                      <span>{result.detail}</span>
                    </span>
                    <Icon name="arrow" />
                  </button>
                ))
              ) : (
                <div className="empty">No matching institution records.</div>
              )}
            </div>
          </div>
        </div>
      ) : null}
      {notificationsOpen ? (
        <div className={`drawer notification-list-drawer ${selectedNotification ? "detail-open" : ""}`}>
          <div className="drawer-head">
            <h3>Institution notifications</h3>
            <div style={{ display: "flex", gap: 6 }}>
              <Button
                small
                onClick={() => {
                  void coreInbox
                    .markAllRead()
                    .then(() => toast("Notifications marked read", "Your notification history was updated."))
                    .catch(() => toast("Notifications unavailable", "Try again later.", "warning"));
                }}
              >
                Mark all read
              </Button>
              <button
                className="icon-button"
                onClick={() => {
                  setNotificationsOpen(false);
                  setSelectedNotification(null);
                }}
                aria-label="Close notifications"
              >
                <Icon name="close" />
              </button>
            </div>
          </div>
          <div className="drawer-body">
            {selectedNotification ? (
              <div className="notification-detail-view">
                <button className="notification-back" onClick={() => setSelectedNotification(null)}>
                  <Icon name="arrow" size={14} /> Back to notifications
                </button>
                <span className="notification-detail-icon">
                  <Icon name="bell" />
                </span>
                <span className="notification-detail-label">Read · {selectedNotification.time}</span>
                <h4>{selectedNotification.title}</h4>
                <p>{selectedNotification.body}</p>
                <Button
                  variant="primary"
                  onClick={() => {
                    const access = new URL(platformEnvironment.appOrigins.auth);
                    const prefix = access.pathname.replace(/\/auth\/?$/, "");
                    if (selectedNotification.deepLink?.startsWith("/"))
                      window.location.assign(access.origin + prefix + selectedNotification.deepLink);
                  }}
                >
                  Open related page
                </Button>
              </div>
            ) : (
              notificationRecords.map((n: any) => (
                <button
                  key={n.id}
                  className={`notification ${n.unread ? "unread" : ""}`}
                  style={{ width: "100%", textAlign: "left", color: "inherit" }}
                  onClick={() => {
                    if (n.unread)
                      void coreInbox
                        .markRead(String(n.id))
                        .catch(() => toast("Notifications unavailable", "Try again later.", "warning"));
                    setSelectedNotification(n);
                  }}
                >
                  <span className="notification-dot" />
                  <span className="notification-copy">
                    <b>{n.title}</b>
                    <p>{n.body}</p>
                    <span>{n.time}</span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
      {languageOpen ? (
        <div className="dropdown" style={{ right: 112 }}>
          <div className="nav-group-label" style={{ paddingTop: 5 }}>
            Primary language
          </div>
          {LANGUAGES.slice(0, 8).map(([name, code]) => (
            <button
              key={name}
              className={`dropdown-item ${primaryLanguage === name ? "active" : ""}`}
              onClick={() => {
                setPrimaryLanguage(name);
                setLanguageOpen(false);
                toast("Primary language updated", `${name} is now the primary interface language.`);
              }}
            >
              <Icon name="globe" size={14} />
              <span style={{ flex: 1 }}>{name}</span>
              <b>{code}</b>
            </button>
          ))}
          <div className="nav-group-label" style={{ paddingTop: 9 }}>
            Secondary translation
          </div>
          <select
            className="select"
            value={secondaryLanguage}
            onChange={e => setSecondaryLanguage(e.target.value)}
          >
            {LANGUAGES.filter(l => l[0] !== primaryLanguage).map(([name]) => (
              <option key={name}>{name}</option>
            ))}
          </select>
        </div>
      ) : null}
      {profileOpen ? (
        <div className="dropdown">
          <button
            className="dropdown-item"
            onClick={() => {
              navigate("settings", "settings-profile");
              setProfileOpen(false);
            }}
          >
            <Icon name="user" />
            <span>Institution profile settings</span>
          </button>
          <button
            className="dropdown-item"
            onClick={() => {
              setModal({ kind: "role" });
              setProfileOpen(false);
            }}
          >
            <Icon name="eye" />
            <span>View assigned roles</span>
          </button>
          <button
            className="dropdown-item"
            onClick={() => {
              setModal({ kind: "institution" });
              setProfileOpen(false);
            }}
          >
            <Icon name="building" />
            <span>Switch institution</span>
          </button>
          <button
            className="dropdown-item"
            onClick={() => {
              setModal({ kind: "help" });
              setProfileOpen(false);
            }}
          >
            <Icon name="help" />
            <span>Help and route guide</span>
          </button>
          <button className="dropdown-item" onClick={signOut}>
            <Icon name="logout" />
            <span>Sign out</span>
          </button>
        </div>
      ) : null}
      <DetailDrawer
        detail={detail}
        spec={detail ? PAGE_SPECS[detail.routeId] : spec}
        role={activeRole}
        onClose={() => setDetail(null)}
        onUpdate={updateDetail}
        onEdit={(editSpec, row, academicAction) =>
          setModal({ kind: "create", payload: { spec: editSpec, record: row, academicAction } })
        }
        onToast={toast}
      />
      <ActionModal
        modal={modal}
        records={records}
        onClose={() => setModal(null)}
        onCreate={createRecord}
        onSaved={() => {
          setModal(null);
          setDetail(null);
          toast("Change recorded", "The directory has been refreshed from the backend.");
        }}
        onToast={toast}
        role={activeRole}
        onRoleChange={switchRole}
      />
      <div className="toast-stack" aria-live="polite">
        {toasts.map(t => (
          <div className="toast" key={t.id}>
            <span className="toast-icon">
              <Icon name={t.tone === "warning" ? "alert" : "check"} size={15} />
            </span>
            <div>
              <b>{t.title}</b>
              <p>{t.message}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default App;
